/* eslint-disable camelcase */
import { MessageBlock } from '@deckee/deck-hand';
import { SHARING_MESSAGE__DEFAULT } from '@deckee/utilities';
import classNames from 'classnames/bind';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import {
  ERROR_MESSAGE_DETAILS_PAGE_EXPIRED,
  ERROR_MESSAGE_DETAILS_PAGE__500,
} from '../../../configurations/error-messages';
import { telemetry } from '../../../services/backend';
import { setSideBar } from '../../../services/store/modules/app-controls';
import PageHolderContent from '../../containers/PageHolderContent/PageHolderContent';
import CommentAndShare from '../../shared/CommentAndShare/CommentAndShare';
import { externalLink, getWeatherAlertIcon } from '../../shared/vector-images';
import CustomDescription from '../../shared/_deprecated/components/CustomDescription/CustomDescription';
import DetailedHeader from '../../shared/_deprecated/components/DetailedHeader/DetailedHeader';
import SourceBanner from '../../shared/_deprecated/components/SourceBanner/SourceBanner';
import Button from '../../shared/_deprecated/elements/Button/Button';
import Heading from '../weather/components/Heading/Heading';
import styles from './DetailsPage.scss';

const cx = classNames.bind(styles);

const DetailsPage = (props) => {
  const {
    dispatchSetSideBar,
    headerBarContent,
    className,
    fetchData,
    errorMessage,
    expiredMessage,
    sharingText,
    sharingItemType,
    sharingPathPrefix,
    type,
  } = props;

  const { id } = useParams();

  const theme = useTheme();

  const [data, setData] = useState();
  const [invalid, setInvalid] = useState(false);
  const [errorDescription, setErrorDescription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [customDescription, setCustomDescription] = useState([]);
  const [informationButtons, setInformationButtons] = useState([]);

  // Setup the share and comment actions
  const sharingPath = `${sharingPathPrefix}${id}`;

  const getIconColors = (subtype) => {
    if (type === 'weather') {
      return {
        fillColor: theme.colors.danger,
      };
    }
    if (subtype === 'notice') {
      return {
        circleColor: '#139ECA',
        fillColor: theme.colors.white,
      };
    }
    return {
      circleColor: theme.colors.warning,
      fillColor: theme.colors.white,
    };
  };

  const sharingType = (dataItem) => {
    if (sharingItemType) return sharingItemType;
    if (dataItem?.markerType) return dataItem.markerType;
    return 'default';
  };

  useEffect(() => {
    let isMounted = true;
    dispatchSetSideBar();
    const getData = async () => {
      setIsLoading(true);
      try {
        const remoteData = await fetchData(id);
        telemetry(
          telemetry.DEBUG,
          `[DetailsPage.getData] received data details: ${JSON.stringify(
            remoteData,
          )}`,
        );
        if (isMounted) {
          setData(remoteData);
        }
      } catch (err) {
        telemetry(
          telemetry.ERROR,
          `[DetailsPage.getData] Error retrieving ${JSON.stringify(err)}`,
        );
        if (isMounted) {
          setErrorDescription({
            message: errorMessage,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    getData();
    return () => {
      telemetry(telemetry.DEBUG, '[DetailsPage.useEffect] unmounted');
      isMounted = false;
    };
  }, [
    setIsLoading,
    setData,
    setErrorDescription,
    dispatchSetSideBar,
    fetchData,
    id,
    errorMessage,
  ]);

  useEffect(() => {
    const currentTimestamp = DateTime.local().toMillis();
    const validToTimestamp = DateTime.fromISO(data?.valid_to).toMillis();
    const timeDiff = validToTimestamp - currentTimestamp;
    setInvalid(timeDiff <= 0);

    if (data?.description) {
      customDescription.push({
        type: 'paragraph',
        content: data?.description,
      });
    }

    if (data?.custom_description) {
      data?.custom_description.forEach((custom) => {
        customDescription.push(custom);
      });
    }

    if (data?.links && data?.links.length > 0) {
      data?.links.forEach((link) =>
        informationButtons.push({
          label: 'More info',
          url: link.url,
          type: 'informationButton',
          icon: 'link',
        }),
      );
    }

    if (data?.videos && data?.videos.length > 0) {
      data?.videos.forEach((link) =>
        customDescription.push({
          type: 'video',
          content: link.url,
        }),
      );
    }
    setCustomDescription(customDescription);
    setInformationButtons(informationButtons);
  }, [customDescription, data, informationButtons]);

  const updatedDate = DateTime.fromISO(data?.updated_at).toRelative();
  const issuedDate = DateTime.fromISO(data?.created_at).toRelative();

  const viewClasses = cx('details-page', {
    [className]: className,
  });

  return (
    <PageHolderContent
      className={viewClasses}
      isLoading={isLoading}
      errorDescription={errorDescription}
      headerBarContent={headerBarContent}
    >
      {/* Add expired message if needed */}
      {invalid && (
        <MessageBlock
          className={cx('details-page__expired-message')}
          type="warning"
        >
          {expiredMessage}
        </MessageBlock>
      )}

      {/* Display icon */}
      <div className={cx('details-page__data-header')}>
        <DetailedHeader
          className={cx('details-page__data-header-details')}
          // type="card"
          iconURL={data?.icon_url}
          icon={getWeatherAlertIcon(data?.subtype || data?.type)}
          iconSize={62}
          iconOptions={getIconColors(data?.type)}
          title={data?.title}
          description={
            updatedDate ? `Updated: ${updatedDate}` : `Created: ${issuedDate}`
          }
        />

        {sharingText && (
          <CommentAndShare
            sharePath={sharingPath}
            shareText={sharingText}
            type={sharingType(data)}
            title={data?.title}
            description={data?.description}
            id={id}
            allowComments={false}
          />
        )}
      </div>

      {/* Show banner for source */}
      {data?.source && (
        <SourceBanner
          className={cx('details-page__source-banner')}
          bannerSource={data?.source}
          official={data?.official}
        />
      )}

      {(data?.description ||
        data?.custom_description ||
        data?.videos ||
        data?.links) && (
        <div className={cx('details-page__description-content')}>
          {/* Header for information text */}
          {/* This header will later be feed in by the api */}
          <Heading
            text="Information"
            className={cx('details-page__description-header')}
          />

          {/* Custom description overrides description */}
          {/* Technically redundant for this use case but potentially not later on */}
          {/* If custom description supplied then show that */}
          {customDescription && (
            <CustomDescription description={customDescription} />
          )}

          {/* Show any information buttons */}
          {informationButtons && informationButtons.length >= 1 && (
            <div className={cx('details-page__info-buttons')}>
              {informationButtons &&
                informationButtons.length >= 1 &&
                informationButtons.map((informationButton) => (
                  <Button
                    className={cx('details-page__info-button')}
                    key={informationButton.url}
                    type="plain"
                    size="medium"
                    round
                    href={informationButton.url}
                    icon={externalLink()}
                  >
                    {informationButton.label}
                  </Button>
                ))}
            </div>
          )}
        </div>
      )}
    </PageHolderContent>
  );
};

DetailsPage.propTypes = {
  className: PropTypes.string,
  dispatchSetSideBar: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  expiredMessage: PropTypes.string,
  headerBarContent: PropTypes.node,
  sharingText: PropTypes.string,
  sharingItemType: PropTypes.string,
  sharingPathPrefix: PropTypes.string,
  type: PropTypes.string,
};

DetailsPage.defaultProps = {
  className: undefined,
  errorMessage: ERROR_MESSAGE_DETAILS_PAGE__500,
  expiredMessage: ERROR_MESSAGE_DETAILS_PAGE_EXPIRED,
  headerBarContent: undefined,
  sharingText: SHARING_MESSAGE__DEFAULT,
  sharingItemType: undefined,
  sharingPathPrefix: undefined,
  type: 'alert',
};

const mapDispatchToProps = (dispatch) => ({
  dispatchSetSideBar: () => dispatch(setSideBar({})),
});

const mapStateToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(DetailsPage);
